/**
 * useAvAVoicePlayback — HiBFF chat TTS + word-reveal playback (2026-07-03)
 *
 * Owns the entire "AvA speaks the reply while the words drip on screen
 * in sync with the audio" behaviour that used to live inline inside
 * `pages/ConversationsChat.js` (playTTS + stopSpeaking + sanitizeForTTS
 * + the revealing-word timer). Extracted so:
 *
 *   1. The chat page stays under 1,500 lines and readable at a glance.
 *   2. This hook can be reused if we ever want to preview an AvA's
 *      voice on the Library / Onboarding screens with the same
 *      SINGLE SOURCE OF TRUTH guarantee.
 *   3. Any future change to the TTS request, the pitch shift, or the
 *      word-drip cadence lives in one file and doesn't require a
 *      1,500-line diff to review.
 *
 * Behaviour is byte-for-byte identical to the previous inline code.
 * The invariants (see `playTTS` header comment) are preserved verbatim:
 *
 *   • Only ONE `<audio>` element exists at any time — enforced by
 *     `useAudioManager` on the calling page.
 *   • `ttsPlayedIds` prevents the same message id from being spoken
 *     twice (StrictMode double-mount protection).
 *   • Component-unmount during any await in playTTS aborts silently
 *     via `isAudioMounted()` checks at every await boundary.
 */
import { useRef, useState } from 'react';
import { TEEN_VOICES } from '../pages/avaLibrary/data';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sanitize a message for TTS playback — strips URLs, markdown image
 * syntax, and raw https:// references so the AvA (esp. Rebecca) never
 * "reads out" a URL letter by letter. The full text (with URLs) still
 * lands in the visible chat bubble + AvAContextDisplay reference panel;
 * only the spoken audio is cleaned. Feb 2026.
 */
const sanitizeForTTS = (raw) => {
  if (!raw || typeof raw !== 'string') return raw || '';
  let out = raw;
  // 1. Markdown image `![alt](url)` → keep alt only, drop the URL.
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  // 2. Markdown link `[text](url)` → keep the text only.
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  // 3. Bare http(s) URLs → replace with a short spoken hint.
  out = out.replace(/https?:\/\/\S+/gi, '');
  // 4. Collapse whitespace so the sanitized text still reads naturally.
  out = out.replace(/\s{2,}/g, ' ').replace(/\s*\n\s*/g, '. ').trim();
  // 5. Trim trailing/leading punctuation islands left by the strip.
  out = out.replace(/^\s*[.,;:—-]+\s*/, '').replace(/\s*[.,;:—-]+\s*$/, '');
  return out;
};

/**
 * @param {{
 *   selectedAva: object|null,
 *   audioRef: React.MutableRefObject<HTMLAudioElement|null>,
 *   attachAudio: (audio: HTMLAudioElement) => boolean,
 *   stopAllAudio: () => void,
 *   isAudioMounted: () => boolean,
 * }} args
 */
export const useAvAVoicePlayback = ({
  selectedAva,
  audioRef,
  attachAudio,
  stopAllAudio,
  isAudioMounted,
}) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [revealingMsgId, setRevealingMsgId] = useState(null);
  const [revealedWords, setRevealedWords] = useState(0);
  const ttsPlayedIds = useRef(new Set()); // Track which message IDs have had TTS played
  const revealIntervalRef = useRef(null);

  /**
   * TTS AUDIO PLAYBACK — SINGLE SOURCE OF TRUTH
   * Only ONE audio element may exist at any time.
   * ttsPlayedIds prevents the same message from being spoken twice.
   */
  const playTTS = async (text, messageId, { replay = false } = {}) => {
    if (messageId && ttsPlayedIds.current.has(messageId) && !replay) return;
    if (messageId) ttsPlayedIds.current.add(messageId);

    // Feb 2026 — never TTS raw URLs (see sanitizeForTTS above). Rebecca
    // shares Milky Way images / evidence links; those go to the reference
    // panel, not the audio track.
    const spokenText = sanitizeForTTS(text);
    if (!spokenText || spokenText.length < 2) {
      // Nothing meaningful to speak (message was pure URL/image) — reveal
      // words instantly and skip the audio round-trip.
      setRevealedWords(9999);
      setRevealingMsgId(null);
      return;
    }

    // Stop anything currently playing before we kick off the new request.
    stopAllAudio();

    try {
      setIsSpeaking(true);

      // Sprint 207P + Feb 2026 teen-pitch fix — the AvA's stored `voice`
      // is a TEEN_VOICES slot id (e.g. "tf3", "tm7"), NOT an OpenAI base
      // voice. Previously we sent the slot id straight to the backend,
      // which silently fell back to raw `nova` (adult). Now we resolve
      // the slot → base voice + pitch and apply the pitch shift via
      // `audio.playbackRate` so the live chat sounds the same as the
      // preview screen.
      const storedVoice = selectedAva?.avatar_config?.voice || 'nova';
      const slot = TEEN_VOICES.find(v => v.id === storedVoice);
      const voice = slot?.baseVoice || (storedVoice in { nova: 1, shimmer: 1, coral: 1, alloy: 1, echo: 1, ash: 1, fable: 1, onyx: 1, sage: 1 } ? storedVoice : 'nova');
      const pitch = slot?.pitch || 1.0;
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: spokenText, voice })
      });

      // Component unmounted (Back button, route change) while we were
      // awaiting the TTS endpoint — abort silently. Without this guard
      // the audio element below would be created + .play()'d on a dead
      // component and the AvA's voice would bleed into the next page.
      if (!isAudioMounted()) return;

      const data = await response.json();

      if (!data.success || !data.audio) {
        if (data.error === 'voice_minutes_exhausted' && !window.__hibffMinutesToastShown) {
          window.__hibffMinutesToastShown = true;
          try {
            const { toast } = await import('sonner');
            toast.warning(data.message || "You're out of voice minutes — top up with a pack.", {
              action: { label: 'Get minutes', onClick: () => { window.location.href = '/hearts'; } },
            });
          } catch (e) { /* noop */ }
        }
        setIsSpeaking(false);
        setRevealedWords(9999);
        setRevealingMsgId(null);
        return;
      }

      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audio.loop = false;
      // Apply teen pitch shift — playbackRate stretches both pitch and
      // tempo, which is the desired youthful effect.
      audio.playbackRate = Math.max(0.5, Math.min(2.0, pitch));
      // attachAudio() refuses + tears down the audio if the component
      // already unmounted between fetch + here.
      if (!attachAudio(audio)) return;

      audio.onended = () => {
        setIsSpeaking(false);
        setRevealedWords(9999);
        setRevealingMsgId(null);
        if (revealIntervalRef.current) clearInterval(revealIntervalRef.current);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        setRevealedWords(9999);
        setRevealingMsgId(null);
        if (revealIntervalRef.current) clearInterval(revealIntervalRef.current);
        audioRef.current = null;
      };

      // Get audio duration for word timing
      await new Promise(resolve => {
        audio.addEventListener('loadedmetadata', resolve, { once: true });
        audio.load();
        setTimeout(resolve, 500);
      });

      // Component unmounted while waiting for metadata — bail.
      if (!isAudioMounted()) return;

      // Reveal counts against the ORIGINAL visible text (URLs included) so
      // the on-screen bubble drips in step with the shorter spoken track.
      const wordCount = text.split(/\s+/).length;

      // Play audio FIRST — then start word reveal synced to actual playback
      await audio.play();

      // Final guard — the cleanup may have fired between `audio.play()`
      // resolving and this line. Pause immediately if we're unmounted.
      if (!isAudioMounted()) {
        try { audio.pause(); audio.src = ''; } catch (e) { /* noop */ }
        return;
      }

      // Use timeupdate to sync words with actual audio position
      if (revealIntervalRef.current) clearInterval(revealIntervalRef.current);
      const updateWords = () => {
        if (!audio.duration) return;
        const progress = audio.currentTime / audio.duration;
        const wordsToShow = Math.ceil(progress * wordCount);
        setRevealedWords(wordsToShow);
      };
      audio.addEventListener('timeupdate', updateWords);
      audio.addEventListener('ended', () => {
        audio.removeEventListener('timeupdate', updateWords);
      }, { once: true });

    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
      // If TTS fails, reveal all text immediately
      setRevealedWords(9999);
      setRevealingMsgId(null);
      if (revealIntervalRef.current) clearInterval(revealIntervalRef.current);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      const audio = audioRef.current;
      audioRef.current = null;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  };

  return {
    // Playback state (read by the render tree)
    isSpeaking,
    revealingMsgId,
    revealedWords,
    // Setters exposed for the caller's own message-send flow to seed
    // the reveal *before* playTTS resolves.
    setRevealingMsgId,
    setRevealedWords,
    setIsSpeaking,
    // Refs — exposed so the caller's unmount cleanup + delete-conversation
    // flow can clear them alongside its own resources.
    ttsPlayedIds,
    revealIntervalRef,
    // Actions
    playTTS,
    stopSpeaking,
  };
};

export default useAvAVoicePlayback;
